import React, { useEffect } from "react";
import { useForm } from "react-hook-form";
import {
    Dialog,
    DialogContent,
    DialogDescription,
    DialogFooter,
    DialogHeader,
    DialogTitle,
} from "components/ui/dialog";
import { Button } from "components/ui/button";
import { Label } from "components/ui/label";
import { Input } from "components/ui/input";
import {
    Form,
    FormField,
    FormItem,
    FormControl,
    FormMessage,
} from "components/ui/form";
import { useRenameGroup } from "../../hooks/use-iam";
import { useTranslation } from "react-i18next";
import { GroupData } from "./iam-group-table";
import { useCommonStore } from "state/store/common/useCommonStore";

interface RenameGroupModalProps {
    isOpen?: boolean;
    onClose: () => void;
    reloadData?: () => void;
    group: GroupData | null;
}

interface FormData {
    groupName: string;
}

export const RenameGroupModal: React.FC<RenameGroupModalProps> = ({
    isOpen = false,
    onClose,
    reloadData,
    group,
}) => {
    const { t } = useTranslation();
    const { mutateAsync: renameGroup } = useRenameGroup();
    const user = useCommonStore((state) => state.user);

    const form = useForm<FormData>({
        defaultValues: {
            groupName: group?.groupName || "",
        },
    });

    const { control, handleSubmit, reset, watch } = form;
    const groupName = watch("groupName");

    useEffect(() => {
        if (isOpen && group) {
            reset({
                groupName: group.groupName || "",
            });
        }
    }, [isOpen, group, reset]);

    const onSubmit = async (data: FormData) => {
        if (!group?.id) return;
        const payload = {
            ItemId: group?.id || "",
            GroupName: data.groupName,
            GroupType: "Project",
            UserIds: group?.users?.map((user: { itemId: string }) => user.itemId) || [],
            OwnerUserId: user?.id || "",
            Source: "internal",
        };

        try {
            await renameGroup(payload);
            reloadData?.();
            onClose();
        } catch (err) {
            console.error(err);
        }
    };

    const stopClick = (e: React.MouseEvent<HTMLDivElement>) => e.stopPropagation();

    return (
        <Dialog open={isOpen} onOpenChange={onClose}>
            <DialogContent className="rounded-[10px] sm:max-w-[500px]" onClick={stopClick}>
                <DialogHeader>
                    <DialogTitle className="mb-2">{t("RENAME_GROUP")}</DialogTitle>
                    <DialogDescription className="text-medium-emphasis font-normal">
                        {t("RENAME_GROUP_DESCRIPTION")}
                    </DialogDescription>
                </DialogHeader>

                <Form {...form}>
                    <form onSubmit={handleSubmit(onSubmit)} className="flex flex-col gap-5">
                        <FormField
                            control={control}
                            name="groupName"
                            rules={{
                                required: t("GROUP_NAME_REQUIRED"),
                                validate: {
                                    notEmpty: (value) =>
                                        value.trim().length > 0 ||
                                        t("GROUP_NAME_CANNOT_BE_EMPTY"),
                                },
                                maxLength: {
                                    value: 50,
                                    message: t("MAX_50_CHARACTERS_GROUP_NAME"),
                                },
                            }}
                            render={({ field }) => (
                                <FormItem>
                                    <Label>{t("GROUP_NAME")}*</Label>
                                    <FormControl>
                                        <Input className="
                                        h-10 w-full rounded-3xl
                                        border border-gray-300
                                        bg-background pr-8
                                        focus:outline-none
                                        focus:border-primary
                                        focus:ring-1 focus:ring-primary
                                    " {...field} placeholder={t("ENTER_NEW_GROUP_NAME")} />
                                    </FormControl>
                                    <FormMessage />
                                </FormItem>
                            )}
                        />

                        <DialogFooter className="mt-5 flex justify-end gap-2">
                            <Button variant="outline" type="button" onClick={onClose}>
                                {t("CANCEL")}
                            </Button>
                            <Button
                                type="submit"
                                disabled={!groupName || groupName.trim().length === 0}
                            >
                                {t("SAVE")}
                            </Button>
                        </DialogFooter>
                    </form>
                </Form>
            </DialogContent>
        </Dialog>
    );
};